/**
 * IT > Notification log at /it/notifications.
 *
 * Every notification Cockpit sent: who got it, on which channel, and whether it
 * arrived. One row per recipient per channel, newest first, in the shared
 * DataGridShell. Filters sit above the grid in one row: type, channel, status,
 * and a date range.
 *
 * Backend: GET /v1/notifications/log (api#665), gated by the it.notifications
 * tool slug server-side. Until it ships the page shows a quiet "Not available
 * yet" state (pattern: useOmni.ts).
 *
 * The Type filter options come from the loaded rows, not from the types
 * endpoint: the log can include types the viewer cannot subscribe to, and the
 * types endpoint only lists the ones they can.
 *
 * Top-level IT tool (no back link, per cockpit-ui-navigation.md).
 */

import { useMemo, useState } from 'react';
import { Bell, Loader2 } from 'lucide-react';
import { Pill } from '@/components/Pill';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { gridTheadClassName } from '@ampersandbrands/ui';
import {
  sendStatusTone,
  typeOptionsFromRows,
  useNotificationLog,
  type NotificationLogFilters,
  type NotificationSend,
} from '@/lib/useNotifications';

const PAGE_SIZE = 200;

const CHANNEL_OPTIONS = [
  { value: '', label: 'All channels' },
  { value: 'teams', label: 'Teams' },
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'sent', label: 'Sent' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'queued', label: 'Queued' },
];

const CHANNEL_LABEL: Record<string, string> = { teams: 'Teams', email: 'Email', sms: 'SMS' };

const controlClass =
  'rounded border border-amp-line bg-amp-white px-2.5 py-1.5 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink';
const labelClass = 'text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted';

function cap(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function formatWhen(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Skip reason or error text for the last column; '-' when neither exists. */
function detailText(s: NotificationSend): string {
  return s.error ?? s.skip_reason ?? '-';
}

export function NotificationLog() {
  const [type, setType] = useState('');
  const [channel, setChannel] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const filters: NotificationLogFilters = {
    type,
    channel,
    status,
    from,
    to,
    limit: PAGE_SIZE,
    offset: 0,
  };
  const { sends, totalCount, available, isLoading } = useNotificationLog(filters);

  // Type options come from the rows on screen. Keep the active selection in the
  // list even when the filtered rows no longer contain it, so the select never
  // shows a blank value.
  const typeOptions = useMemo(() => {
    const opts = typeOptionsFromRows(sends);
    if (type && !opts.some((o) => o.value === type)) opts.push({ value: type, label: type });
    return [{ value: '', label: 'All types' }, ...opts];
  }, [sends, type]);

  const colCount = 7;

  const body = isLoading ? (
    <tr>
      <td colSpan={colCount} className="px-4 py-6 text-center text-amp-ink-muted dark:text-amp-dark-ink-muted">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
        Loading notifications...
      </td>
    </tr>
  ) : !available ? (
    <tr>
      <td colSpan={colCount} className="px-4 py-6 text-center text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Not available yet. The notification log will fill in once notifications are switched on.
      </td>
    </tr>
  ) : sends.length === 0 ? (
    <tr>
      <td colSpan={colCount} className="px-4 py-6 text-center text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
        No notifications match these filters.
      </td>
    </tr>
  ) : (
    sends.map((s) => (
      <tr key={s.id} className="border-b border-amp-line last:border-0 dark:border-amp-dark-line">
        <td
          className="whitespace-nowrap px-3 py-2 text-xs tabular-nums text-amp-ink-muted dark:text-amp-dark-ink-muted"
          title={s.created_at ? `Created ${formatWhen(s.created_at)}` : undefined}
        >
          {formatWhen(s.sent_at ?? s.created_at)}
        </td>
        <td className="px-3 py-2 text-xs text-amp-ink dark:text-amp-dark-ink">{s.type_label}</td>
        <td className="whitespace-nowrap px-3 py-2 text-xs text-amp-ink dark:text-amp-dark-ink">
          {s.recipient_email || '-'}
        </td>
        <td className="whitespace-nowrap px-3 py-2 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
          {CHANNEL_LABEL[s.channel] ?? s.channel}
        </td>
        <td className="px-3 py-2 text-xs">
          <Pill tone={sendStatusTone(s.status)}>{cap(s.status)}</Pill>
        </td>
        <td className="px-3 py-2 text-xs text-amp-ink dark:text-amp-dark-ink">
          {s.event?.deep_link ? (
            <a
              href={s.event.deep_link}
              className="underline hover:no-underline"
              title={s.event.summary || undefined}
            >
              {s.event.title || s.event.deep_link}
            </a>
          ) : (
            (s.event?.title ?? '-')
          )}
        </td>
        <td className="px-3 py-2 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
          {detailText(s)}
        </td>
      </tr>
    ))
  );

  return (
    <div className="space-y-6">
      <div>
        <p className="mb-1 text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">
          IT
        </p>
        <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">
          Notification log
        </h1>
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          Every notification Cockpit sent: who, which channel, and whether it arrived.
        </p>
      </div>

      {/* Filters, one row */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className={labelClass} htmlFor="notif-type">
            Type
          </label>
          <select id="notif-type" value={type} onChange={(e) => setType(e.target.value)} className={controlClass}>
            {typeOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className={labelClass} htmlFor="notif-channel">
            Channel
          </label>
          <select id="notif-channel" value={channel} onChange={(e) => setChannel(e.target.value)} className={controlClass}>
            {CHANNEL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className={labelClass} htmlFor="notif-status">
            Status
          </label>
          <select id="notif-status" value={status} onChange={(e) => setStatus(e.target.value)} className={controlClass}>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className={labelClass} htmlFor="notif-from">
            From
          </label>
          <input
            id="notif-from"
            type="date"
            value={from}
            max={to || undefined}
            onChange={(e) => setFrom(e.target.value)}
            className={controlClass}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={labelClass} htmlFor="notif-to">
            To
          </label>
          <input
            id="notif-to"
            type="date"
            value={to}
            min={from || undefined}
            onChange={(e) => setTo(e.target.value)}
            className={controlClass}
          />
        </div>
      </div>

      <DataGridShell
        title={
          <span className="inline-flex items-center gap-2">
            <Bell className="h-4 w-4 text-amp-olive dark:text-amp-olive-dark" />
            Notifications
          </span>
        }
        loaded={isLoading || !available ? undefined : sends.length}
        total={isLoading || !available ? undefined : totalCount}
        countNoun="notifications"
      >
        <table className="min-w-full">
          <thead className={gridTheadClassName}>
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium">sent</th>
              <th className="px-3 py-2 text-left text-xs font-medium">type</th>
              <th className="px-3 py-2 text-left text-xs font-medium">recipient</th>
              <th className="px-3 py-2 text-left text-xs font-medium">channel</th>
              <th className="px-3 py-2 text-left text-xs font-medium">status</th>
              <th className="px-3 py-2 text-left text-xs font-medium">title</th>
              <th className="px-3 py-2 text-left text-xs font-medium">error or skip reason</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">{body}</tbody>
        </table>
      </DataGridShell>
    </div>
  );
}
